import ForensicEquipmentAPI from "@/api/equipment";
import { FilterDefinition, Operators } from "@monolith-forensics/monolith-ui";

type EquipmentFilterPage<T> = T[];

const EquipmentFilterDefs: FilterDefinition[] = [
  {
    dataField: "asset_number",
    label: "Asset Number",
    inputType: "text",
    operators: [
      Operators.Equals,
      Operators.Contains,
      Operators.IsEmpty,
      Operators.IsNotEmpty,
    ],
  },
  {
    dataField: "barcode",
    label: "Barcode",
    inputType: "text",
    operators: [
      Operators.Equals,
      Operators.Contains,
      Operators.IsEmpty,
      Operators.IsNotEmpty,
    ],
  },
  {
    dataField: "vendor",
    label: "Vendor",
    inputType: "multiselect",
    operators: [Operators.In, Operators.NIn],
    query: {
      initialPageParam: 1,
      cacheKey: ({ search }) => JSON.stringify(["equipment", "vendors"]),
      loadPage: async ({ pageParam, search }) => {
        const page =
          ((await ForensicEquipmentAPI.getEquipmentVendors({
            page: pageParam,
          })) as EquipmentFilterPage<{ vendor: string }>) ?? [];

        return {
          items:
            page.map((vendor) => ({
              label: vendor.vendor,
              value: vendor.vendor,
            })) || [],
          nextPageParam: null,
        };
      },
    },
  },
  {
    dataField: "name",
    label: "Name",
    pluralLabel: "Names",
    inputType: "multiselect",
    operators: [Operators.In, Operators.NIn],
    query: {
      initialPageParam: 1,
      cacheKey: ({ search }) => JSON.stringify(["equipment", "names"]),
      loadPage: async ({ pageParam, search }) => {
        const page = (await ForensicEquipmentAPI.getEquipmentNames({
          page: pageParam,
        })) as EquipmentFilterPage<{ name: string }>;

        return {
          items:
            page.map((item) => ({
              label: item.name,
              value: item.name,
            })) || [],
          nextPageParam: null,
        };
      },
    },
  },

  {
    dataField: "category",
    label: "Category",
    inputType: "text",
    operators: [
      Operators.Equals,
      Operators.NotEquals,
      Operators.IsEmpty,
      Operators.IsNotEmpty,
    ],
  },
  {
    dataField: "model",
    label: "Model",
    pluralLabel: "Models",
    inputType: "multiselect",
    operators: [Operators.In, Operators.NIn],
    query: {
      initialPageParam: 1,
      cacheKey: ({ search }) => JSON.stringify(["equipment", "models"]),
      loadPage: async ({ pageParam, search }) => {
        const page = (await ForensicEquipmentAPI.getEquipmentModels({
          page: pageParam,
        })) as EquipmentFilterPage<{ model: string }>;

        return {
          items:
            page.map((model) => ({
              label: model.model,
              value: model.model,
            })) || [],
          nextPageParam: null,
        };
      },
    },
  },
  {
    dataField: "serial_number",
    label: "Serial Number",
    inputType: "text",
    operators: [
      Operators.Equals,
      Operators.Contains,
      Operators.IsEmpty,
      Operators.IsNotEmpty,
    ],
  },
  {
    dataField: "purchase_date",
    label: "Purchase Date",
    inputType: "date",
    operators: [
      Operators.Equals,
      Operators.LessThan,
      Operators.GreaterThan,
      Operators.Between,
      Operators.IsEmpty,
      Operators.IsNotEmpty,
    ],
  },
  {
    dataField: "cost",
    label: "Cost",
    inputType: "number",
    operators: [
      Operators.Equals,
      Operators.LessThan,
      Operators.GreaterThan,
      Operators.Between,
      Operators.IsEmpty,
      Operators.IsNotEmpty,
    ],
  },
  {
    dataField: "location",
    label: "Location",
    pluralLabel: "Locations",
    inputType: "multiselect",
    operators: [Operators.In, Operators.NIn],
    query: {
      initialPageParam: 1,
      cacheKey: ({ search }) => JSON.stringify(["equipment", "locations"]),
      loadPage: async ({ pageParam, search }) => {
        const page = (await ForensicEquipmentAPI.getEquipmentLocations({
          page: pageParam,
        })) as EquipmentFilterPage<{ location: string }>;

        return {
          items:
            page?.map((location) => ({
              label: location.location,
              value: location.location,
            })) || [],
          nextPageParam: null,
        };
      },
    },
  },
  {
    dataField: "description",
    label: "Description",
    inputType: "text",
    operators: [Operators.Contains, Operators.IsEmpty, Operators.IsNotEmpty],
  },
];

export default EquipmentFilterDefs;
